import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '../../../store/rootReducer';
import { IApplication } from '../../../Interfaces/Application/IApplication';
import CustomFieldForm from './CustomFieldForm';
import {ApplicationDispatch, getNewSignedUrl} from '../../../store/application/types';
import { useBusinessRulesEngine } from '../../../Data/BusinessRulesData';
import { getApplicant, updateCustomerCustomFields } from "../../../utils/appUtils";
import { Radio, FormControlLabel, FormLabel, IconButton, RadioGroup, TextField, Tooltip } from '@mui/material';
import { Refresh } from "@mui/icons-material";
import { ICustomValuePair } from "../../../Interfaces/Application/ICustomValuePair";
import { useConfiguration } from "../../../Data/ConfigurationData";
import { hasValue } from "@amsanalytics/react-shared-library";
import { CustomerCreditPage } from "./CustomerCreditPage";
import { BureauFavorite } from '../../../Data/BureauFavorites';
import { TextBox } from '@progress/kendo-react-inputs';
import { Notification } from '@progress/kendo-react-notification';
import { Constants } from '../../../utils/constants';
import { isMissingMismoXml } from '../../../utils/creditReportUtils';

interface BureauPageProps {
  workNumber: boolean | undefined;
}

const BureauPage: React.FC<BureauPageProps> = (props) => {
  const { data: configuration } = useConfiguration();
  const currentApp: IApplication = useSelector((state: AppState) => state.applications.editingApp!);
  const favorites: BureauFavorite[] = useSelector((state: AppState) => state.applications.lookups.bureauFavorites.favorites);
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const cobuyer1 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant1Id, true);
  const cobuyer2 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant2Id, true);
  const primaryCredit = primary?.customer?.customerCredit[0];
  const coApplicant1Credit = cobuyer1?.customer?.customerCredit[0];
  const coApplicant2Credit = cobuyer2?.customer?.customerCredit[0];
  const customerCredits = useMemo(
    () => [primaryCredit, coApplicant1Credit, coApplicant2Credit],
    [primaryCredit, coApplicant1Credit, coApplicant2Credit]
  );

  // Read signed URLs from top-level state (loaded once when app opens in OrderEditor)
  const signedUrls = useSelector((state: AppState) => state.applications.signedUrls);
  const getSignedUrl = (creditId?: string) => creditId ? (signedUrls[creditId.toLowerCase()] ?? signedUrls[creditId]) : undefined;

  // The selected customer credit. Prefer one that already has a signed URL loaded.
  const firstAvailableCredit = customerCredits.find(cc => cc?.id && getSignedUrl(cc.id)) ?? primaryCredit ?? coApplicant1Credit ?? coApplicant2Credit;
  const [selectedCustomerCreditId, setSelectedCustomerCreditId] = useState<string | undefined>(firstAvailableCredit?.id);
  const selectedCustomerCredit = customerCredits.find(cc => cc?.id === selectedCustomerCreditId) ?? firstAvailableCredit;

  const handleRadioButtonEvent = (customerCreditId: string): void => {
    const foundCustomerCredit = customerCredits.find((cc) => cc?.id === customerCreditId);
    setSelectedCustomerCreditId(foundCustomerCredit?.id);
  };

  const customerCreditPulledAfterCutoverDate = selectedCustomerCredit && configuration && (new Date(selectedCustomerCredit.modifiedDate) > new Date(configuration.losBureauPageXmlCutoverDate));

  const customFieldsToUse = currentApp?.appCustomers.find(ac => ac.customerId === selectedCustomerCredit?.customerId)?.customer.customerCustomFields.filter(b => b.customField.isCustomerCustomField);

  // MISMO enablement is account-level at 700Credit, so warn if any applicant's pulled credit lacks it
  const showMismoMissingWarning = useMemo(
    () => !!configuration?.isSuperUser &&
      customerCredits.some(cc => cc && hasValue(cc.creditPulledDate) && isMissingMismoXml(cc.creditSummaryXml)),
    [configuration?.isSuperUser, customerCredits]
  );

  const mismoMissingWarning = showMismoMissingWarning && (
    <Notification type={{ style: 'warning', icon: true }} style={{ width: 'fit-content', marginBottom: '5px' }}>
      Credit was pulled successfully, but the report does not contain MISMO XML. This is needed for the discrete, broken out trade line values below. Kathy also needs them for reporting purposes. Please reach out to Sal and Support at 700Credit to get MISMO XML enabled, and include the client's 700Credit username in the email for client identification purposes.
    </Notification>
  );

  const workNumberValue = selectedCustomerCredit?.creditSummaryXml?.includes("<WN_Report>Hit</WN_Report>")
    ? "Hit"
    : selectedCustomerCredit?.creditSummaryXml?.includes("<WN_Report>No-Hit</WN_Report>")
      ? "No Hit"
      : selectedCustomerCredit?.creditSummaryXml?.includes("WN_Report")
        ? "No Work Number data in XML"
        : undefined;

  return getSignedUrl(primaryCredit?.id) || getSignedUrl(coApplicant1Credit?.id) || getSignedUrl(coApplicant2Credit?.id) ? (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: 'calc(100dvh - 112px)', overflowY: 'hidden', overflowX: 'auto' }}>
      {mismoMissingWarning}
      {getSignedUrl(coApplicant1Credit?.id) && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FormLabel component='legend' style={{ fontWeight: 'bold', marginRight: '1rem' }}>View Credit For: </FormLabel>
          <RadioGroup
            aria-label='customer credit toggle'
            name='customer credit toggle'
            value={selectedCustomerCredit?.id}
            onChange={(e) => {
              handleRadioButtonEvent(e.target.value);
            }}
            row
          >
            <FormControlLabel
              id='primaryCreditRadio'
              value={primaryCredit?.id}
              control={<Radio color='primary' />}
              label='Primary Applicant'
              labelPlacement='end'
            />
            <FormControlLabel
              id='coApplicant1CreditRadio'
              value={coApplicant1Credit?.id}
              control={<Radio color='primary' />}
              label='Co-Applicant 1'
              labelPlacement='end'
            />
            {getSignedUrl(coApplicant2Credit?.id) && (
              <FormControlLabel
                id='coApplicant2CreditRadio'
                value={coApplicant2Credit?.id}
                control={<Radio color='primary' />}
                label='Co-Applicant 2'
                labelPlacement='end'
              />
            )}
          </RadioGroup>
        </div>
      )}
      <div style={{ display: 'flex', flex: 1, height: '100%' }}>
        <div style={{ maxWidth: '800px', minWidth: '800px' }}>
          <iframe
            src={getSignedUrl(selectedCustomerCredit?.id)}
            style={{
              height: '100%',
              width: '100%',
              maxWidth: '800px',
              overflow: 'scroll',
            }}
          />
        </div>
        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: '100%', minWidth: '375px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {selectedCustomerCredit &&
              <div>
                <Tooltip
                  title={"Refresh the Bureau Iframe"}
                  placement="top"
                >
                  <IconButton
                    size="small"
                    style={{ backgroundColor: "lightgray" }}
                    onClick={() => { dispatch(getNewSignedUrl(currentApp.id, selectedCustomerCredit.id)) }}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            }
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '100px' }}>
              <small>EQ</small>
              <TextBox title='Equifax Score' value={selectedCustomerCredit?.eqScore ?? 'N/A'} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '100px' }}>
              <small>EX</small>
              <TextBox title='Experian Score' value={selectedCustomerCredit?.exScore ?? 'N/A'} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '100px' }}>
              <small>TU</small>
              <TextBox title='TransUnion Score' value={selectedCustomerCredit?.tuScore ?? 'N/A'} />
            </div>
          </div>
          {hasValue(workNumberValue) &&
            <div>
              <TextField
                label='Work Number Verification'
                style={{ width: "225px" }}
                value={workNumberValue}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </div>
          }
          {customerCreditPulledAfterCutoverDate ?
            <CustomerCreditPage
              // Remount when the selected applicant changes so no input retains the previous applicant's value.
              key={selectedCustomerCredit!.id}
              customerCredit={selectedCustomerCredit!}
              customFields={customFieldsToUse ?? []}
              favorites={favorites}
            />
            :
            <CustomFieldForm
              selectedCustomerId={selectedCustomerCredit!.customerId}
              showScoreColumn={false}
              onUpdateApplicationCustomField={(field: ICustomValuePair) => {
                if (currentApp && selectedCustomerCredit) {
                  updateCustomerCustomFields(selectedCustomerCredit.customerId, [{ formattedValue: field.formattedValue, customFieldName: field.customFieldName, valueType: field.valueType }], currentApp, dispatch, businessRulesEngine);
                }
              }}
              entityCustomFields={customFieldsToUse}
              isBureauPage={true}
              title={"Custom Fields"}
            />
          }
        </div>
      </div>
    </div>
  ) : (
    <div>
      {mismoMissingWarning}
      <h3 style={{ marginLeft: '10px' }}>No credit report available</h3>
    </div>
  );
};

export default BureauPage;
